// Review and publish. Sync finds documents in the chosen sources; an HR admin reads what was
// extracted and approves it (or excludes the document); Publish makes approved documents the
// set Ask HR answers from. Nothing reaches employees until it is published.

import { Fragment, useEffect, useState } from "react";
import {
  MAX_VARIANT_GROUP_CHARS,
  REVIEW_LABELS,
  isArchived,
  type CorpusVersion,
  type HrDocument,
  type Page,
  type ReviewStatus,
  type SyncSummary,
} from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, type ApplicabilityValue, type SharePointSource } from "@ask-hr/core/sources";
import { ApiError, api } from "../api.js";
import { Seg } from "../components/Seg.js";
import { VariantGroups } from "./VariantGroups.js";

type Doc = HrDocument & { status: ReviewStatus };
type VersionSummary = Omit<CorpusVersion, "entries">;
interface Named {
  id: string;
  name: string;
}
interface CorpusInfo {
  current: (VersionSummary & { pointer: { version: number; setBy: string; setAt: string } }) | null;
  versions: VersionSummary[];
  pending: { added: Named[]; updated: Named[]; removed: Named[] };
  approvedCount: number;
  budget: number;
}
interface SyncInfo {
  last: SyncSummary | null;
  running: { trigger: string; startedAt: string } | null;
}

// Documents in Archive folders only show under Archive, unless HR approved one: then it is part of
// what Ask HR answers from, and shows with the other approved documents too.
type Filter = "review" | "approved" | "all" | "problems" | "excluded" | "archive" | "removed";
const main = (d: Doc) => !isArchived(d) || d.status === "approved";
const FILTERS: Record<Filter, (d: Doc) => boolean> = {
  review: (d) => main(d) && (d.status === "new" || d.status === "changed"),
  approved: (d) => d.status === "approved",
  all: (d) => main(d) && d.status !== "removed",
  problems: (d) => main(d) && d.status === "error",
  excluded: (d) => main(d) && d.status === "excluded",
  archive: (d) => isArchived(d) && d.status !== "removed",
  removed: (d) => d.status === "removed",
};

const PILL: Record<ReviewStatus, string> = {
  new: "pill alert",
  changed: "pill alert",
  approved: "pill",
  excluded: "pill ok",
  removed: "pill ok",
  error: "pill bad",
};

const ERRORS: Record<string, string> = {
  no_text: "No text found. It may be a scanned image; save it as a text PDF or a Word file.",
  too_large: "The file is over 40 MB.",
  unreadable: "The file couldn't be opened. It may be damaged or password-protected.",
  unsupported: "Only PDF and Word (.docx) files can be read.",
  web_too_large: "The page is over 5 MB.",
  web_type: "The address didn't return a readable web page.",
  web_blocked: "The page redirects to an address Ask HR won't fetch.",
  web_unreachable: "The website couldn't be reached. The next sync will try again.",
};
const errorText = (code?: string) =>
  code && ERRORS[code]
    ? ERRORS[code]
    : code?.startsWith("graph_")
      ? "SharePoint didn't return the file. The next sync will try again."
      : code?.startsWith("web_")
        ? "The website returned an error. The next sync will try again."
        : "Something went wrong reading this file. The next sync will try again.";

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const day = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "");
const APPLICABILITY = Object.entries(APPLICABILITY_LABELS) as [ApplicabilityValue, string][];

export function DocumentsPage() {
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [sources, setSources] = useState<SharePointSource[]>([]);
  const [corpus, setCorpus] = useState<CorpusInfo | null>(null);
  const [syncInfo, setSyncInfo] = useState<SyncInfo | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState("");
  const [filter, setFilterState] = useState<Filter>("review");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAudience, setBulkAudience] = useState("");
  const [grouping, setGrouping] = useState(false);
  const [groupName, setGroupName] = useState("");
  const setFilter = (f: Filter) => {
    setFilterState(f);
    setSelected(new Set());
    setGrouping(false);
  };
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const fetchAll = () =>
    Promise.all([
      api<{ documents: Doc[] }>("GET", "/api/admin/documents"),
      api<{ sources: SharePointSource[] }>("GET", "/api/admin/sources"),
      api<CorpusInfo>("GET", "/api/admin/corpus"),
      api<SyncInfo>("GET", "/api/admin/sync"),
    ]);

  const apply = ([d, s, c, y]: Awaited<ReturnType<typeof fetchAll>>) => {
    setDocs(d.documents);
    setSources(s.sources);
    setCorpus(c);
    setSyncInfo(y);
    setError(null);
  };

  const reload = async () => {
    try {
      apply(await fetchAll());
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    let live = true;
    fetchAll().then(
      (r) => live && apply(r),
      (e: unknown) => live && setError(e),
    );
    return () => {
      live = false;
    };
    // First load only; later loads go through reload().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While a sync runs, check every 5 seconds, then refresh the list when it finishes.
  const running = Boolean(syncInfo?.running);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      api<SyncInfo>("GET", "/api/admin/sync").then(
        (y) => {
          setSyncInfo(y);
          if (!y.running) void reload();
        },
        () => {},
      );
    }, 5000);
    return () => clearInterval(t);
    // reload is recreated each render; the interval only needs the running flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  async function act(key: string, fn: () => Promise<unknown>, done: string) {
    setBusy(key);
    setStatus("");
    try {
      await fn();
      setStatus(done);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "changed_since_review") {
        setStatus("This document changed in SharePoint since you opened it. Review the new version.");
        setOpen(null);
        await reload();
      } else if (e instanceof ApiError && e.body.error === "already_running") {
        setStatus("A sync is already running.");
        await reload();
      } else {
        setError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  const sourceOf = new Map(sources.map((s) => [s.id, s]));
  // Variant groups: their names (to pick from), and whether each one's versions actually differ in
  // who they apply to. All the same means nobody gets a different version: worth a warning.
  const groupAudiences = new Map<string, Set<string>>();
  for (const d of docs ?? []) {
    if (!d.variantGroup || d.status === "removed") continue;
    const who = d.applicability ?? sourceOf.get(d.sourceId)?.applicability ?? "";
    groupAudiences.set(d.variantGroup, (groupAudiences.get(d.variantGroup) ?? new Set()).add(who));
  }
  const groupNames = [...groupAudiences.keys()].sort((a, b) => a.localeCompare(b));
  const counts = Object.fromEntries(
    (Object.keys(FILTERS) as Filter[]).map((f) => [f, docs?.filter(FILTERS[f]).length ?? 0]),
  ) as Record<Filter, number>;
  const q = query.trim().toLowerCase();
  const shown = (docs ?? [])
    .filter(FILTERS[filter])
    .filter((d) => !q || `${d.name} ${d.driveName}${d.path} ${d.variantGroup ?? ""}`.toLowerCase().includes(q))
    .sort((a, b) => `${a.driveName}${a.path}/${a.name}`.localeCompare(`${b.driveName}${b.path}/${b.name}`));
  const chosen = shown.filter((d) => selected.has(d.id));
  const approvable = chosen.filter((d) => (d.status === "new" || d.status === "changed") && d.current);
  const allShownSelected = shown.length > 0 && shown.every((d) => selected.has(d.id));
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function bulk(
    action: "approve" | "exclude" | "include" | "applicability" | "group",
    applicability?: ApplicabilityValue | null,
    variantGroup?: string | null,
  ) {
    const targets = action === "approve" ? approvable : chosen;
    if (!targets.length) return;
    setBusy("bulk");
    setStatus("");
    try {
      const r = await api<{ results: { id: string; ok: boolean; reason?: string }[] }>("POST", "/api/admin/documents/bulk", {
        action,
        items: targets.map((d) => ({ id: d.id, ...(action === "approve" ? { hash: d.current?.hash } : {}) })),
        ...(action === "applicability" ? { applicability: applicability ?? null } : {}),
        ...(action === "group" ? { variantGroup: variantGroup ?? null } : {}),
      });
      const done = r.results.filter((x) => x.ok).length;
      const changed = r.results
        .filter((x) => x.reason === "changed_since_review")
        .map((x) => targets.find((d) => d.id === x.id)?.name ?? x.id);
      const verb = {
        approve: "Approved",
        exclude: "Excluded",
        include: "Brought back",
        applicability: "Updated",
        group: variantGroup ? `Grouped as "${variantGroup}":` : "Ungrouped",
      }[action];
      setStatus(
        `${verb} ${done} ${done === 1 ? "document" : "documents"}.` +
          (changed.length
            ? ` Skipped ${changed.length} that changed in SharePoint since this page loaded: ${changed.join(", ")}. Review those again.`
            : "") +
          (action === "approve" || action === "applicability" || action === "group" ? " Publish to make it live." : ""),
      );
      setSelected(new Set());
      setBulkAudience("");
      setGrouping(false);
      setGroupName("");
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const pending = corpus?.pending;
  const pendingCount = pending ? pending.added.length + pending.updated.length + pending.removed.length : 0;

  return (
    <>
      {error ? <ErrorNotice error={error} /> : null}
      <p className="status" role="status">
        {status}
      </p>

      <section className="panel" aria-labelledby="publish-h">
        <h2 id="publish-h">Published for answers</h2>
        {corpus?.current ? (
          <p>
            <strong>Version {corpus.current.version}</strong>
            <span className="muted">
              {" "}
              · {corpus.current.documentCount} documents · published {when(corpus.current.publishedAt)} by{" "}
              {corpus.current.publishedBy}
              {corpus.current.pointer.setAt !== corpus.current.publishedAt
                ? ` · made current ${when(corpus.current.pointer.setAt)} by ${corpus.current.pointer.setBy}`
                : ""}
            </span>
          </p>
        ) : (
          <p className="sub">Nothing is published yet. Ask HR has no documents to answer from until you publish.</p>
        )}
        {corpus?.current ? <Budget tokens={corpus.current.tokens} budget={corpus.budget} /> : null}

        {pending && pendingCount > 0 ? (
          <div className="pending">
            <p>
              <strong>Ready to publish:</strong> {listNames("added", pending.added)}
              {listNames("updated", pending.updated)}
              {listNames("removed", pending.removed)}
            </p>
            {confirmPublish ? (
              <div className="actions start">
                <button
                  type="button"
                  className="primary"
                  disabled={busy !== null}
                  onClick={() =>
                    void act("publish", () => api("POST", "/api/admin/corpus/publish"), "Published. Ask HR now answers from the new version.").then(
                      () => setConfirmPublish(false),
                    )
                  }
                >
                  {busy === "publish" ? "Publishing…" : `Publish ${corpus?.approvedCount ?? 0} approved documents`}
                </button>
                <button type="button" onClick={() => setConfirmPublish(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" className="primary" onClick={() => setConfirmPublish(true)}>
                Publish…
              </button>
            )}
          </div>
        ) : corpus ? (
          <p className="sub">{corpus.approvedCount ? "Everything approved is published." : "Approve documents below, then publish them here."}</p>
        ) : null}

        {corpus && corpus.versions.length > 1 ? (
          <details className="sec inner">
            <summary>
              <h3>Earlier versions</h3>
            </summary>
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Version</th>
                    <th scope="col">Published</th>
                    <th scope="col">Documents</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {corpus.versions.map((v) => (
                    <tr key={v.version}>
                      <td>{v.version}</td>
                      <td className="muted">
                        {when(v.publishedAt)} · {v.publishedBy}
                      </td>
                      <td>{v.documentCount}</td>
                      <td className="right">
                        {v.version === corpus.current?.version ? (
                          <span className="pill">In use</span>
                        ) : (
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() =>
                              void act(
                                `v${v.version}`,
                                () => api("POST", "/api/admin/corpus/rollback", { version: v.version }),
                                `Ask HR now answers from version ${v.version}.`,
                              )
                            }
                          >
                            Use version {v.version}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ) : null}
      </section>

      <section className="panel" aria-labelledby="sync-h">
        <h2 id="sync-h">Sync sources</h2>
        <div className="toolbar">
          <span>
            {syncInfo?.running ? (
              <>Syncing now (started {when(syncInfo.running.startedAt)})…</>
            ) : syncInfo?.last ? (
              <>
                Last sync {when(syncInfo.last.finishedAt)}
                <span className="muted"> ({syncInfo.last.trigger === "schedule" ? "hourly" : "manual"})</span>: {syncInfo.last.new} new,{" "}
                {syncInfo.last.changed} changed, {syncInfo.last.removed} removed
                {syncInfo.last.errors + syncInfo.last.sourceErrors ? `, ${syncInfo.last.errors + syncInfo.last.sourceErrors} problems` : ""}
              </>
            ) : (
              <span className="muted">No sync has run yet. It runs every hour, or now.</span>
            )}
          </span>
          <button
            type="button"
            disabled={busy !== null || running}
            onClick={() => void act("sync", () => api("POST", "/api/admin/sync"), "Sync started. This page updates when it finishes.")}
          >
            {running ? "Syncing…" : "Sync now"}
          </button>
        </div>
      </section>

      {docs ? (
        <VariantGroups
          docs={docs}
          sources={sources}
          onShow={(group) => {
            setFilter("all");
            setQuery(group);
            document.getElementById("docs-h")?.scrollIntoView({ block: "start" });
          }}
        />
      ) : null}

      <section className="panel" aria-labelledby="docs-h">
        <h2 id="docs-h">Documents</h2>
        <div className="toolbar">
          <Seg
            label="Show"
            value={filter}
            onChange={setFilter}
            options={[
              ["review", `Needs review (${counts.review})`],
              ["approved", `Approved (${counts.approved})`],
              ["all", `All (${counts.all})`],
              ["problems", `Problems (${counts.problems})`],
              ["excluded", `Excluded (${counts.excluded})`],
              ["archive", `Archive (${counts.archive})`],
              ["removed", `Removed (${counts.removed})`],
            ]}
          />
          <label className="search">
            <span className="sr-only">Search documents</span>
            <input type="search" placeholder="Search by title or folder" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
        </div>

        {filter === "archive" ? (
          <p className="sub">
            Documents in folders named Archive. They stay out of Needs review; approve one here if Ask HR should use it.
          </p>
        ) : null}

        {chosen.length ? (
          <div className="bulkbar" role="region" aria-label="Actions for selected documents">
            <strong>{chosen.length} selected</strong>
            <button type="button" className="primary" disabled={busy !== null || !approvable.length} onClick={() => void bulk("approve")}>
              {approvable.length === chosen.length ? "Approve" : `Approve ${approvable.length} needing review`}
            </button>
            <button type="button" disabled={busy !== null} onClick={() => void bulk("exclude")}>
              Exclude
            </button>
            {chosen.some((d) => d.status === "excluded") ? (
              <button type="button" disabled={busy !== null} onClick={() => void bulk("include")}>
                Include again
              </button>
            ) : null}
            <label>
              <span className="sr-only">Set who the selected documents apply to</span>
              <select
                value={bulkAudience}
                disabled={busy !== null}
                onChange={(e) => {
                  const v = e.target.value;
                  setBulkAudience(v);
                  if (v) void bulk("applicability", v === "source" ? null : (v as ApplicabilityValue));
                }}
              >
                <option value="">Set applies to…</option>
                <option value="source">Same as source</option>
                {APPLICABILITY.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" disabled={busy !== null} aria-expanded={grouping} onClick={() => setGrouping(!grouping)}>
              Group as location variants…
            </button>
            {chosen.some((d) => d.variantGroup) ? (
              <button type="button" disabled={busy !== null} onClick={() => void bulk("group", undefined, null)}>
                Ungroup
              </button>
            ) : null}
            <button type="button" className="linkbtn" onClick={() => setSelected(new Set())}>
              Clear
            </button>
            {grouping ? (
              <form
                className="group-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (groupName.trim()) void bulk("group", undefined, groupName.trim());
                }}
              >
                <p className="sub">
                  Versions of one document for different places, like a general and a California employee guide. Each employee
                  gets only the version for where they work; one set to All employees is the version for everyone else. Set each
                  version's Applies to, then publish.
                </p>
                <label>
                  <span className="sr-only">Group name</span>
                  <input
                    list="variant-groups"
                    placeholder="Group name, e.g. Employee Guide"
                    maxLength={MAX_VARIANT_GROUP_CHARS}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    autoFocus
                  />
                </label>
                <datalist id="variant-groups">
                  {groupNames.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
                <button type="submit" className="primary" disabled={busy !== null || !groupName.trim()}>
                  Group {chosen.length}
                </button>
                <button type="button" onClick={() => setGrouping(false)}>
                  Cancel
                </button>
              </form>
            ) : null}
          </div>
        ) : null}

        {docs === null ? (
          error ? null : <p className="sub">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="empty">
            {filter === "review"
              ? docs.length
                ? "Nothing needs review."
                : "No documents yet. Choose sources on the Sources tab, then sync."
              : q
                ? `Nothing matches "${query.trim()}".`
                : filter === "archive"
                  ? "No documents in Archive folders."
                  : "None."}
          </p>
        ) : (
          <div className="tablewrap tall">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="pick">
                    <input
                      type="checkbox"
                      aria-label="Select all shown"
                      checked={allShownSelected}
                      onChange={() => setSelected(allShownSelected ? new Set() : new Set(shown.map((d) => d.id)))}
                    />
                  </th>
                  <th scope="col">Document</th>
                  <th scope="col">Applies to</th>
                  <th scope="col">Status</th>
                  <th scope="col">Modified</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => {
                  const src = sourceOf.get(d.sourceId);
                  return (
                    <Fragment key={d.id}>
                      <tr className={selected.has(d.id) ? "picked" : undefined}>
                        <td className="pick">
                          <input type="checkbox" aria-label={`Select ${d.name}`} checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                        </td>
                        <td>
                          <a href={d.webUrl} target="_blank" rel="noreferrer">
                            {d.name}
                          </a>
                          <div className="muted small">
                            {d.driveName}
                            {d.path === "/" ? "" : d.path}
                          </div>
                          {d.variantGroup ? (
                            <div className="variant-tag small">
                              <button type="button" className="linkbtn" title="Show this group" onClick={() => setQuery(d.variantGroup ?? "")}>
                                Location variants: {d.variantGroup}
                              </button>
                              {(groupAudiences.get(d.variantGroup)?.size ?? 0) < 2 ? (
                                <span className="muted"> · every version applies to the same people</span>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {src && d.status !== "removed" ? (
                            <select
                              aria-label={`Who ${d.name} applies to`}
                              value={d.applicability ?? ""}
                              disabled={busy !== null}
                              onChange={(e) => {
                                const value = (e.target.value || null) as ApplicabilityValue | null;
                                void act(
                                  d.id,
                                  () => api("PATCH", `/api/admin/documents/${encodeURIComponent(d.id)}`, { applicability: value }),
                                  `${d.name} now applies to: ${APPLICABILITY_LABELS[value ?? src.applicability]}.${d.approved ? " Publish to make it live." : ""}`,
                                );
                              }}
                            >
                              <option value="">Same as source ({APPLICABILITY_LABELS[src.applicability]})</option>
                              {APPLICABILITY.map(([v, l]) => (
                                <option key={v} value={v}>
                                  {l}
                                </option>
                              ))}
                            </select>
                          ) : src ? (
                            APPLICABILITY_LABELS[d.applicability ?? src.applicability]
                          ) : (
                            <span className="muted">Source removed</span>
                          )}
                        </td>
                        <td>
                          <span className={PILL[d.status]}>{REVIEW_LABELS[d.status]}</span>
                          {d.status === "error" ? <div className="muted small">{errorText(d.error)}</div> : null}
                        </td>
                        <td className="muted">{day(d.modified)}</td>
                        <td className="right">
                          {d.current && d.status !== "removed" ? (
                            <button type="button" aria-expanded={open === d.id} onClick={() => setOpen(open === d.id ? null : d.id)}>
                              {open === d.id ? "Close" : d.status === "new" || d.status === "changed" ? "Review" : "View text"}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {open === d.id && d.current ? (
                        <tr className="detail">
                          <td colSpan={6}>
                            <Review
                              doc={d}
                              busy={busy}
                              onApprove={() =>
                                void act(
                                  d.id,
                                  () => api("POST", `/api/admin/documents/${encodeURIComponent(d.id)}/approve`, { hash: d.current?.hash }),
                                  `Approved ${d.name}. Publish to make it live.`,
                                ).then(() => setOpen(null))
                              }
                              onExclude={(exclude) =>
                                void act(
                                  d.id,
                                  () => api("POST", `/api/admin/documents/${encodeURIComponent(d.id)}/${exclude ? "exclude" : "include"}`),
                                  exclude ? `Excluded ${d.name}.` : `${d.name} is back for review.`,
                                ).then(() => setOpen(null))
                              }
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function listNames(what: string, items: Named[]) {
  if (!items.length) return null;
  const names = items.slice(0, 5).map((i) => i.name).join(", ");
  return (
    <span className="pending-group">
      {items.length} {what} <span className="muted">({names}{items.length > 5 ? `, and ${items.length - 5} more` : ""})</span>{" "}
    </span>
  );
}

function Budget({ tokens, budget }: { tokens: CorpusVersion["tokens"]; budget: number }) {
  const rows = [
    ["California", tokens["us-ca"]],
    ["US outside California", tokens["us-other"]],
    ["Canada", tokens.canada],
  ] as const;
  const max = Math.max(...rows.map(([, n]) => n));
  return (
    <p className="muted small">
      Size of what each group's answers read:{" "}
      {rows.map(([label, n], i) => (
        <span key={label}>
          {i ? " · " : ""}
          {label} {Math.round((n / budget) * 100)}%
        </span>
      ))}{" "}
      of the limit.
      {max > budget * 0.8 ? <strong> Close to the limit: tell IT before adding much more.</strong> : null}
    </p>
  );
}

function Review({
  doc,
  busy,
  onApprove,
  onExclude,
}: {
  doc: Doc;
  busy: string | null;
  onApprove: () => void;
  onExclude: (exclude: boolean) => void;
}) {
  const [pages, setPages] = useState<Page[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const hash = doc.current?.hash;

  useEffect(() => {
    let live = true;
    api<{ pages: Page[] }>("GET", `/api/admin/documents/${encodeURIComponent(doc.id)}/text`).then(
      (r) => live && setPages(r.pages),
      (e: unknown) => live && setError(e),
    );
    return () => {
      live = false;
    };
  }, [doc.id, hash]);

  const reviewing = doc.status === "new" || doc.status === "changed";
  return (
    <div className="review">
      <p className="sub">
        {doc.status === "changed"
          ? `A new version from ${day(doc.current?.modified)}. Ask HR keeps using the version approved ${day(doc.approvedAt)} by ${doc.approvedBy} until you approve this one and publish.`
          : doc.status === "new"
            ? "This is the text Ask HR will read. Check it came through correctly and belongs in answers."
            : doc.status === "approved"
              ? `Approved ${day(doc.approvedAt)} by ${doc.approvedBy}.`
              : doc.status === "excluded"
                ? `Excluded ${day(doc.excludedAt)} by ${doc.excludedBy}. Ask HR won't use it.`
                : null}
        {doc.current ? ` ${doc.current.pages} ${doc.current.pages === 1 ? "section" : "sections"}.` : ""}
      </p>
      {error ? <ErrorNotice error={error} /> : null}
      {pages === null && !error ? <p className="sub">Loading text…</p> : null}
      {pages ? (
        <div className="extract" tabIndex={0} aria-label={`Text of ${doc.name}`}>
          {pages.map((p) => (
            <section key={p.n}>
              <h4>{p.label}</h4>
              <p>{p.text}</p>
            </section>
          ))}
        </div>
      ) : null}
      <div className="actions start">
        {reviewing ? (
          <button type="button" className="primary" disabled={busy !== null || !pages} onClick={onApprove}>
            Approve this version
          </button>
        ) : null}
        {doc.status === "excluded" ? (
          <button type="button" disabled={busy !== null} onClick={() => onExclude(false)}>
            Include again
          </button>
        ) : (
          <button type="button" disabled={busy !== null} onClick={() => onExclude(true)}>
            Exclude from Ask HR
          </button>
        )}
      </div>
    </div>
  );
}

function ErrorNotice({ error }: { error: unknown }) {
  let text = "Something went wrong. Try again, and tell IT if it keeps happening.";
  if (error instanceof ApiError) {
    if (error.status === 403) text = "You need the HR.Admin role for this.";
    else if (error.body.error === "missing_text") text = "Some approved documents are missing their text. Run a sync, then publish again.";
    else if (error.body.error === "publish_failed") text = "Publishing failed. Nothing changed for employees. Try again.";
  }
  return (
    <p className="notice" role="alert">
      {text}
    </p>
  );
}
